'use client'

import { io, Socket } from 'socket.io-client'
import React from 'react'
import useTokenPrices from '@/utils/hooks/useTokenPrices'

let cachedSocket: Socket | null = null

const getSocket = (): Socket => {
	if (cachedSocket) return cachedSocket
	cachedSocket = io('https://tap.trac.network/', {
		autoConnect: true,
		reconnection: true,
		reconnectionDelay: 500,
		reconnectionDelayMax: 500,
		randomizationFactor: 0
	})
	return cachedSocket
}

interface TapToken {
	ticker: string
	overall_balance: number
	transferable_balance: string | number
	available_balance: number
	collection: { slug: string; floor_price_per?: number; icon?: string } | { slug: string }
	type: string
}

export default function useTapBalance(address: string) {
	const { data: tokenPrices } = useTokenPrices()

	const [_isConnected, setIsConnected] = React.useState(false)
	const [_events, setEvents] = React.useState<any[]>([])
	const [tokens, setTokens] = React.useState<TapToken[]>([])

	React.useEffect(() => {
		if (!address) return
		const socket = getSocket()
		requestAnimationFrame(() => {
			setIsConnected(socket.connected)
		})
		function onConnect() {
			setIsConnected(true)
		}

		function onDisconnect() {
			setIsConnected(false)
		}

		function onEvent(value: { func: string; result: any; args: any[] }) {
			setEvents((previous) => [...previous, value])

			if (value.func === 'accountTokens') {
				const tickers = value.result

				for (const ticker of tickers) {
					socket.emit('get', {
						func: 'balance',
						args: [address, ticker],
						call_id: ''
					})
				}
			}

			if (value.func === 'transferable') {
				if (value.result === '0' || !value.result) {
					return
				}

				setTokens((prev) => {
					const item = prev.find((e) => e.ticker === value.args[1].toUpperCase())
					const isDmt = value.args[1].startsWith('dmt')
					const transferable_balance = parseInt(value.result, 10) / (isDmt ? 1 : 1e18)

					return [
						...prev.filter((e) => e.ticker !== value.args[1].toUpperCase()),
						{
							...item,
							ticker: value.args[1].toUpperCase(),
							type: item?.type ?? 'tap',
							collection: item?.collection ?? { slug: `tap-${value.args[1].toUpperCase()}` },
							transferable_balance,
							available_balance: (item?.overall_balance ?? 0) - transferable_balance,
							overall_balance: item?.overall_balance ?? 0
						}
					]
				})
			}

			if (value.func == 'balance') {
				socket.emit('get', {
					func: 'transferable',
					args: [address, value.args[1]],
					call_id: ''
				})

				setTokens((previous) => [
					...previous.filter((e) => e.ticker !== value.args[1].toUpperCase()),
					{
						overall_balance:
							parseInt(value.result, 10) / (value.args[1].startsWith('dmt') ? 1 : 1e18),
						ticker: value.args[1].toUpperCase(),
						transferable_balance: '0',
						available_balance:
							parseInt(value.result, 10) / (value.args[1].startsWith('dmt') ? 1 : 1e18),
						collection: (tokenPrices || []).find(
							(e) => `tap-${value.args[1].toUpperCase()}` === e.slug
						) || { slug: `tap-${value.args[1].toUpperCase()}` },
						type: 'tap'
					}
				])
			}
		}

		socket.emit('get', {
			func: 'accountTokens',
			args: [address, 0, 500],
			call_id: ''
		})

		socket.on('connect', onConnect)
		socket.on('disconnect', onDisconnect)
		socket.on('response', onEvent)

		return () => {
			socket.off('connect', onConnect)
			socket.off('disconnect', onDisconnect)
			socket.off('response', onEvent)
		}
	}, [address, tokenPrices])

	return tokens
}
